import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  Switch,
  FormControl,
  FormHelperText,
} from "@mui/material";
import axios from "axios";
import API_URL from "../../config";
import { useAuth } from "../../components/Context/AuthContext";
import API_BASE_URL from "../../utils/api";
import { Link as RouterLink } from "react-router-dom";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EventIcon from "@mui/icons-material/Event";

const GOLD = "#FFD700";

const AdminViewUsers = () => {
  useEffect(() => {
    document.title = "Manage Users - MS Digital Hub";
  }, []);
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogType, setDialogType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(`${API_BASE_URL}/auth/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUsers(res.data);
  };

  const openDialog = (type, user, value = null) => {
    setDialogType(type);
    setSelectedUser(user);
    setPendingValue(value);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogType("");
    setSelectedUser(null);
    setPendingValue(null);
    setDialogOpen(false);
  };

  const confirmAction = async () => {
    if (!selectedUser) return;
    const token = localStorage.getItem("token");

    try {
      if (dialogType === "role") {
        await axios.patch(
          `${API_BASE_URL}/auth/update-role/${selectedUser._id}`,
          { role: pendingValue },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else if (dialogType === "notifications") {
        if (selectedUser.emailOptOut) {
          await axios.get(`${API_BASE_URL}/auth/opt-in/${selectedUser._id}`);
        } else {
          await axios.get(`${API_BASE_URL}/auth/opt-out/${selectedUser._id}`);
        }
      } else if (dialogType === "delete") {
        await axios.delete(`${API_BASE_URL}/auth/user/${selectedUser._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      fetchUsers();
    } catch (error) {
      console.error("Admin action failed", error);
    } finally {
      closeDialog();
    }
  };

  return (
    <Box
      sx={{
        width: "auto",
        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* Title, subtitle, pill — all centered */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h4">User Management</Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage roles, notifications, and identify grantees at a glance.
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: "inline-block",
              mt: 0.75,
              px: 1.4,
              py: 0.3,
              borderRadius: 999,
              bgcolor: "#f5f5f5",
              border: "1px solid #e0e0e0",
              fontWeight: 500,
            }}
          >
            Admin-only view
          </Typography>
        </Box>

        {/* Grants overview button – aligned right */}
        <Box
          sx={{
            mt: 2,

            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<EventIcon sx={{ color: GOLD }} />}
            component={RouterLink}
            to="/transcribeathon-admin"
            sx={{
              borderColor: GOLD,
              "&:hover": {
                borderColor: GOLD,
                bgcolor: "#fff9d6",
              },
            }}
          >
            Manage Transcribeathon
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<WorkspacePremiumIcon sx={{ color: GOLD }} />}
            component={RouterLink}
            to="/grants-overview"
            sx={{
              borderColor: GOLD,
              "&:hover": {
                borderColor: GOLD,
                bgcolor: "#fff9d6",
              },
            }}
          >
            Grants overview
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<WorkspacePremiumIcon sx={{ color: GOLD }} />}
            component={RouterLink}
            to="/admin/contribution-analytics"
            sx={{
              borderColor: GOLD,
              "&:hover": {
                borderColor: GOLD,
                bgcolor: "#fff9d6",
              },
            }}
          >
            Transcription Contributions
          </Button>
        </Box>
      </Box>
      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Notifications</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u._id}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>{u.name}</Typography>
                  {u.grantsAccess && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: "#fff9d6",
                        border: `1px solid ${GOLD}66`,
                      }}
                      title="Grants participant"
                    >
                      <WorkspacePremiumIcon
                        fontSize="small"
                        sx={{ color: GOLD }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, textTransform: "uppercase" }}
                      >
                        Grant
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TableCell>

              <TableCell>{u.email}</TableCell>

              <TableCell>
                <FormControl>
                  <Select
                    value={u.role}
                    onChange={(e) => openDialog("role", u, e.target.value)}
                    disabled={
                      !currentUser?.isSuperAdmin ||
                      u.isSuperAdmin ||
                      u._id === currentUser?._id
                    }
                    size="small"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                  <FormHelperText>Change Role</FormHelperText>
                </FormControl>
              </TableCell>

              <TableCell>
                <Switch
                  checked={!u.emailOptOut}
                  onChange={() => openDialog("notifications", u)}
                  size="small"
                  color="primary"
                />
              </TableCell>

              <TableCell>
                <Button
                  onClick={() => openDialog("delete", u)}
                  color="error"
                  size="small"
                  disabled={!currentUser?.isSuperAdmin || u.isSuperAdmin}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* Confirm dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogType === "role" &&
              (pendingValue === "admin"
                ? `Are you sure you want to promote ${selectedUser?.name} to Admin?`
                : `Are you sure you want to demote ${selectedUser?.name} to User?`)}
            {dialogType === "notifications" &&
              `${selectedUser?.name} will be ${
                selectedUser?.emailOptOut
                  ? "subscribed to"
                  : "unsubscribed from"
              } thread notifications.`}
            {dialogType === "delete" &&
              `Are you sure you want to delete ${selectedUser?.name}? This action is irreversible.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={confirmAction} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminViewUsers;
